import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import { catchError, of, switchMap, take } from 'rxjs';
import { DetailedInstaller } from '~gc/domains/models/detailed-installer';
import { Installer, InstallerTeamLead, isInstallerLead, User } from '~gc/domains/users/user/user.model';
import { authenticatedRole } from '../../../domains/app/auth/auth.selectors';
import { isRole } from '../../../domains/app/auth/auth.maps';
import {
  CompanyInsightsService,
  FLAG_SEVERITY_COLORS,
  FLAG_SEVERITY_LABELS,
  flagSummary,
  InstallerFlag,
  topSeverity,
} from '../../../domains/company-insights/company-insights.service';
import { UsersUIFacade } from '../../../domains/users';
import { selectedInstallerTeamLeadProfile } from '../../../domains/users/users-ui.actions';
import { DetailedWorkOrder } from '../../../domains/work/models/detailed-work-order';
import { WorkOrder } from '../../../domains/work/work-order/work-order.model';
import { vtiRankFor } from '../../pipes/vti-rank.pipe';

@Component({
  selector: 'gc-user-header',
  templateUrl: './user-header.component.html',
  styleUrls: ['./user-header.component.scss'],
})
export class UserHeaderComponent implements OnChanges {
  @Input() isLoading = false;
  @Input() user?: User | DetailedInstaller;
  @Input() workOrder?: WorkOrder | DetailedWorkOrder;
  @Input() centered = true;

  /** TT-259: the viewing company's own flags on this installer. */
  flags: InstallerFlag[] = [];
  private flagsFor?: string;

  constructor(
    public usersUI: UsersUIFacade,
    public readonly store: Store,
    private readonly insights: CompanyInsightsService,
    private readonly alerts: AlertController,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this.loadFlags();
    }
  }

  get flagColor(): string | undefined {
    const top = topSeverity(this.flags);
    return top ? FLAG_SEVERITY_COLORS[top] : undefined;
  }

  get flagText(): string {
    return flagSummary(this.flags);
  }

  async showFlags(): Promise<void> {
    const alert = await this.alerts.create({
      header: 'Your company flagged this installer',
      message: this.flags
        .map(flag => `${FLAG_SEVERITY_LABELS[flag.severity]}: ${flag.issue}`)
        .join('<br/>'),
      buttons: ['Close'],
    });
    await alert.present();
  }

  validInsurance(user: InstallerTeamLead) {
    return user.insuranceExpiration > formatISO(Date.now());
  }

  meetsHammerRatingThreshold(user: DetailedInstaller) {
    return ((user as DetailedInstaller).keyHammerRating ?? 0) >= (this.workOrder?.minimumHammerRatingThreshold ?? 0);
  }

  showInstallerProfile(installer: DetailedInstaller) {
    this.usersUI.showInstallerProfile(installer as Installer);
  }

  showInstallerTeamLeadProfile(installer: DetailedInstaller) {
    this.store.dispatch(selectedInstallerTeamLeadProfile({ installer: installer as InstallerTeamLead }));
  }

  /**
   * TT-238: the rank for a VTI value, in the same bands the backend uses when it
   * scores an installer (VtiRatingService.getRank), so the pill and the Dashboard agree.
   */
  rankFor(value?: number | null): string {
    return vtiRankFor(value);
  }

  isDetailedInstaller<T extends User>(user: User): user is DetailedInstaller {
    return isInstallerLead(user) ? typeof (user as DetailedInstaller).keyHammerRating === 'number' : false;
  }

  /** Only a company sees flags, and only its own; installers never ask. */
  private loadFlags(): void {
    const id = this.user && isInstallerLead(this.user as User) ? (this.user as User).id : undefined;
    if (!id || id === this.flagsFor) return;
    this.flagsFor = id;
    this.flags = [];
    this.store
      .select(authenticatedRole)
      .pipe(
        take(1),
        switchMap(role =>
          isRole(role, 'companyAdmin', 'companyManager') ? this.insights.flags(id) : of([] as InstallerFlag[]),
        ),
        catchError(() => of([] as InstallerFlag[])),
      )
      .subscribe(flags => (this.flags = flags));
  }
}
